import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Search,
  Filter,
  Download,
  Upload,
  Users,
  Calendar,
  Clock,
  BarChart2,
  PieChart,
  Sliders,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  User,
  Mail,
  Phone,
  DollarSign,
  Percent,
  Tag,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Plus,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/Button';
import RiskScoreDistributionChart from '../../components/risk/RiskScoreDistributionChart';
import LeadSourceChart from '../../components/risk/LeadSourceChart';
import ConversionMetricsChart from '../../components/risk/ConversionMetricsChart';
import PortfolioValueChart from '../../components/risk/PortfolioValueChart';
import AssignLeadModal from '../../components/risk/AssignLeadModal';
import AddNoteModal from '../../components/risk/AddNoteModal';
import ScheduleFollowUpModal from '../../components/risk/ScheduleFollowUpModal';
import RiskProfileModal from '../../components/risk/RiskProfileModal';
import ImportRiskProspectsModal from '../../components/risk/ImportRiskProspectsModal';

interface RiskProspect {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  portfolio_value: number;
  risk_score: number;
  asset_allocation: {
    stocks: number;
    bonds: number;
    cash: number;
    other: number;
  };
  submission_date: string;
  lead_status: 'new' | 'contacted' | 'qualified' | 'unqualified' | 'converted';
  lead_source: string;
  assigned_to?: string;
  notes?: string[];
  follow_up_date?: string;
}

interface FilterState {
  riskMin: number;
  riskMax: number;
  portfolioMin: number;
  portfolioMax: number;
  dateRange: '7d' | '30d' | '90d' | 'all';
  leadStatus: string;
  leadSource: string;
}

const RiskNumberProspects: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<keyof RiskProspect>('submission_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<FilterState>({
    riskMin: 0,
    riskMax: 100,
    portfolioMin: 0,
    portfolioMax: 10000000,
    dateRange: 'all',
    leadStatus: 'all',
    leadSource: 'all'
  });
  
  // Modals state
  const [selectedProspect, setSelectedProspect] = useState<RiskProspect | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showRiskProfileModal, setShowRiskProfileModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Action menu state
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

  // Fetch risk prospects
  const { data: prospects, isLoading, refetch } = useQuery({
    queryKey: ['riskProspects', filters],
    queryFn: async () => {
      // In a real implementation, this would fetch from your database with proper filtering
      // For this demo, we'll return mock data
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Generate mock data
      const mockData: RiskProspect[] = Array.from({ length: 50 }, (_, i) => {
        const riskScore = Math.floor(Math.random() * 100);
        const portfolioValue = Math.floor(Math.random() * 2000000) + 50000;
        const stockAllocation = Math.floor(Math.random() * 80) + 10;
        const bondAllocation = Math.floor(Math.random() * (90 - stockAllocation)) + 5;
        const cashAllocation = Math.floor(Math.random() * (95 - stockAllocation - bondAllocation)) + 2;
        const otherAllocation = 100 - stockAllocation - bondAllocation - cashAllocation;
        
        const leadSources = ['whatismyrisknumber.com', 'Website', 'Referral', 'Seminar', 'Social Media'];
        const leadStatuses: RiskProspect['lead_status'][] = ['new', 'contacted', 'qualified', 'unqualified', 'converted'];
        
        // Generate a date within the last 90 days
        const daysAgo = Math.floor(Math.random() * 90);
        const submissionDate = new Date();
        submissionDate.setDate(submissionDate.getDate() - daysAgo);
        
        return {
          id: `risk-${i + 1}`,
          first_name: ['John', 'Jane', 'Michael', 'Sarah', 'Robert', 'Emily', 'David', 'Lisa'][Math.floor(Math.random() * 8)],
          last_name: ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Garcia'][Math.floor(Math.random() * 8)],
          email: `prospect${i + 1}@example.com`,
          phone: `(${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
          portfolio_value: portfolioValue,
          risk_score: riskScore,
          asset_allocation: {
            stocks: stockAllocation,
            bonds: bondAllocation,
            cash: cashAllocation,
            other: otherAllocation
          },
          submission_date: submissionDate.toISOString(),
          lead_status: leadStatuses[Math.floor(Math.random() * leadStatuses.length)],
          lead_source: leadSources[Math.floor(Math.random() * leadSources.length)],
          assigned_to: Math.random() > 0.6 ? ['Sarah Johnson', 'Michael Brown', 'David Wilson'][Math.floor(Math.random() * 3)] : undefined,
          notes: Math.random() > 0.7 ? [
            `Initial contact made on ${format(new Date(submissionDate.getTime() + 86400000), 'MMM d, yyyy')}`,
            `Discussed retirement goals on ${format(new Date(submissionDate.getTime() + 172800000), 'MMM d, yyyy')}`
          ] : undefined,
          follow_up_date: Math.random() > 0.6 ? new Date(submissionDate.getTime() + Math.floor(Math.random() * 10 + 1) * 86400000).toISOString() : undefined
        };
      });
      
      return mockData;
    }
  });

  // Update lead status mutation
  const updateLeadStatus = useMutation({
    mutationFn: async ({ prospectId, status }: { prospectId: string; status: RiskProspect['lead_status'] }) => {
      // In a real implementation, this would update the database
      // For this demo, we'll just simulate a successful update
      await new Promise(resolve => setTimeout(resolve, 500));
      return { prospectId, status };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['riskProspects', filters], (oldData: RiskProspect[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(prospect => 
          prospect.id === data.prospectId 
            ? { ...prospect, lead_status: data.status } 
            : prospect
        );
      });
      toast.success(`Lead status updated to ${data.status}`);
    },
    onError: () => {
      toast.error('Failed to update lead status');
    }
  });

  // Assign lead mutation
  const assignLead = useMutation({
    mutationFn: async ({ prospectId, assignee }: { prospectId: string; assignee: string }) => {
      // In a real implementation, this would update the database
      await new Promise(resolve => setTimeout(resolve, 500));
      return { prospectId, assignee };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['riskProspects', filters], (oldData: RiskProspect[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(prospect => 
          prospect.id === data.prospectId 
            ? { ...prospect, assigned_to: data.assignee } 
            : prospect
        );
      });
      toast.success(`Lead assigned to ${data.assignee}`);
      setShowAssignModal(false);
    },
    onError: () => {
      toast.error('Failed to assign lead');
    }
  });

  // Add note mutation
  const addNote = useMutation({
    mutationFn: async ({ prospectId, note }: { prospectId: string; note: string }) => {
      // In a real implementation, this would update the database
      await new Promise(resolve => setTimeout(resolve, 500));
      return { prospectId, note };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['riskProspects', filters], (oldData: RiskProspect[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(prospect => {
          if (prospect.id === data.prospectId) {
            const updatedNotes = prospect.notes ? [...prospect.notes, data.note] : [data.note];
            return { ...prospect, notes: updatedNotes };
          }
          return prospect;
        });
      });
      toast.success('Note added successfully');
      setShowNoteModal(false);
    },
    onError: () => {
      toast.error('Failed to add note');
    }
  });

  // Schedule follow-up mutation
  const scheduleFollowUp = useMutation({
    mutationFn: async ({ prospectId, date }: { prospectId: string; date: string }) => {
      // In a real implementation, this would update the database
      await new Promise(resolve => setTimeout(resolve, 500));
      return { prospectId, date };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['riskProspects', filters], (oldData: RiskProspect[] | undefined) => {
        if (!oldData) return [];
        return oldData.map(prospect => 
          prospect.id === data.prospectId 
            ? { ...prospect, follow_up_date: data.date } 
            : prospect
        );
      });
      toast.success(`Follow-up scheduled for ${format(new Date(data.date), 'MMM d, yyyy')}`);
      setShowFollowUpModal(false);
    },
    onError: () => {
      toast.error('Failed to schedule follow-up');
    }
  });

  // Export to CSV
  const handleExportCSV = () => {
    if (!prospects || prospects.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Create CSV headers
      const headers = [
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Portfolio Value',
        'Risk Score',
        'Stocks %',
        'Bonds %',
        'Cash %',
        'Other %',
        'Submission Date',
        'Lead Status',
        'Lead Source',
        'Assigned To',
        'Follow-up Date'
      ];

      // Create CSV rows
      const rows = prospects.map(prospect => [
        prospect.first_name,
        prospect.last_name,
        prospect.email,
        prospect.phone,
        prospect.portfolio_value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
        prospect.risk_score,
        prospect.asset_allocation.stocks + '%',
        prospect.asset_allocation.bonds + '%',
        prospect.asset_allocation.cash + '%',
        prospect.asset_allocation.other + '%',
        format(new Date(prospect.submission_date), 'MM/dd/yyyy'),
        prospect.lead_status,
        prospect.lead_source,
        prospect.assigned_to || '',
        prospect.follow_up_date ? format(new Date(prospect.follow_up_date), 'MM/dd/yyyy') : ''
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `risk-number-prospects-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Export successful');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };

  // Handle sort
  const handleSort = (field: keyof RiskProspect) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort prospects
  const filteredProspects = prospects
    ? prospects
        .filter(prospect => {
          // Search filter
          const searchLower = searchTerm.toLowerCase();
          const matchesSearch = searchTerm === '' || 
            `${prospect.first_name} ${prospect.last_name}`.toLowerCase().includes(searchLower) ||
            prospect.email.toLowerCase().includes(searchLower) ||
            prospect.phone.includes(searchTerm);
          
          // Risk score filter
          const matchesRiskScore = 
            prospect.risk_score >= filters.riskMin && 
            prospect.risk_score <= filters.riskMax;
          
          // Portfolio value filter
          const matchesPortfolioValue = 
            prospect.portfolio_value >= filters.portfolioMin && 
            prospect.portfolio_value <= filters.portfolioMax;
          
          // Date range filter
          let matchesDateRange = true;
          if (filters.dateRange !== 'all') {
            const submissionDate = new Date(prospect.submission_date);
            const now = new Date();
            const daysToSubtract = 
              filters.dateRange === '7d' ? 7 : 
              filters.dateRange === '30d' ? 30 : 
              filters.dateRange === '90d' ? 90 : 0;
            
            const cutoffDate = new Date();
            cutoffDate.setDate(now.getDate() - daysToSubtract);
            
            matchesDateRange = submissionDate >= cutoffDate;
          }
          
          // Lead status filter
          const matchesLeadStatus = 
            filters.leadStatus === 'all' || 
            prospect.lead_status === filters.leadStatus;
          
          // Lead source filter
          const matchesLeadSource = 
            filters.leadSource === 'all' || 
            prospect.lead_source === filters.leadSource;
          
          return matchesSearch && 
                 matchesRiskScore && 
                 matchesPortfolioValue && 
                 matchesDateRange && 
                 matchesLeadStatus &&
                 matchesLeadSource;
        })
        .sort((a, b) => {
          // Handle different field types
          if (sortField === 'portfolio_value' || sortField === 'risk_score') {
            return sortDirection === 'asc' 
              ? a[sortField] - b[sortField]
              : b[sortField] - a[sortField];
          } else if (sortField === 'submission_date' || sortField === 'follow_up_date') {
            const dateA = a[sortField] ? new Date(a[sortField]).getTime() : 0;
            const dateB = b[sortField] ? new Date(b[sortField]).getTime() : 0;
            return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
          } else {
            // String comparison for other fields
            const valueA = String(a[sortField] || '').toLowerCase();
            const valueB = String(b[sortField] || '').toLowerCase();
            return sortDirection === 'asc'
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA);
          }
        })
    : [];

  // Get unique lead sources for filter dropdown
  const leadSources = prospects 
    ? [...new Set(prospects.map(p => p.lead_source))]
    : [];

  // Get lead status counts for stats
  const leadStatusCounts = prospects
    ? prospects.reduce((acc, prospect) => {
        acc[prospect.lead_status] = (acc[prospect.lead_status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    : {};

  // Calculate average risk score
  const averageRiskScore = prospects && prospects.length > 0
    ? Math.round(prospects.reduce((sum, p) => sum + p.risk_score, 0) / prospects.length)
    : 0;

  // Calculate total portfolio value
  const totalPortfolioValue = prospects
    ? prospects.reduce((sum, p) => sum + p.portfolio_value, 0)
    : 0;

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get sort icon
  const getSortIcon = (field: keyof RiskProspect) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-primary" />
      : <ArrowDown className="h-4 w-4 text-primary" />;
  };

  // Get status badge
  const getStatusBadge = (status: RiskProspect['lead_status']) => {
    switch (status) {
      case 'new':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            New
          </span>
        );
      case 'contacted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Contacted
          </span>
        );
      case 'qualified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Qualified
          </span>
        );
      case 'unqualified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unqualified
          </span>
        );
      case 'converted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Converted
          </span>
        );
      default:
        return null;
    }
  };

  // Get risk score color
  const getRiskScoreColor = (score: number) => {
    if (score < 30) return 'text-green-600 bg-green-100';
    if (score < 50) return 'text-blue-600 bg-blue-100';
    if (score < 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Risk Number Prospects</h1>
          <p className="mt-1 text-gray-600">
            Manage and analyze leads from whatismyrisknumber.com
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            className="flex items-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {showFilters ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>
          <Button 
            variant="outline" 
            className="flex items-center"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button 
            variant="outline" 
            className="flex items-center"
            onClick={handleExportCSV}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            className="flex items-center"
            onClick={() => refetch()}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{filteredProspects.length}</h3>
          <p className="text-gray-600">Total Prospects</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{leadStatusCounts['converted'] || 0}</h3>
          <p className="text-gray-600">Converted Leads</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Sliders className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{averageRiskScore}</h3>
          <p className="text-gray-600">Avg. Risk Score</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{formatCurrency(totalPortfolioValue)}</h3>
          <p className="text-gray-600">Total Portfolio Value</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search prospects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Risk Score Range
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.riskMin}
                    onChange={(e) => setFilters({ ...filters, riskMin: parseInt(e.target.value) || 0 })}
                    className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                  />
                  <span>to</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={filters.riskMax}
                    onChange={(e) => setFilters({ ...filters, riskMax: parseInt(e.target.value) || 100 })}
                    className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Portfolio Value
                </label>
                <div className="flex items-center space-x-2">
                  <div className="relative flex-grow">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      value={filters.portfolioMin}
                      onChange={(e) => setFilters({ ...filters, portfolioMin: parseInt(e.target.value) || 0 })}
                      className="pl-8 w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <span>to</span>
                  <div className="relative flex-grow">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="number"
                      min="0"
                      value={filters.portfolioMax}
                      onChange={(e) => setFilters({ ...filters, portfolioMax: parseInt(e.target.value) || 10000000 })}
                      className="pl-8 w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Submission Date
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => setFilters({ ...filters, dateRange: e.target.value as FilterState['dateRange'] })}
                  className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Time</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Status
                </label>
                <select
                  value={filters.leadStatus}
                  onChange={(e) => setFilters({ ...filters, leadStatus: e.target.value })}
                  className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="unqualified">Unqualified</option>
                  <option value="converted">Converted</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Source
                </label>
                <select
                  value={filters.leadSource}
                  onChange={(e) => setFilters({ ...filters, leadSource: e.target.value })}
                  className="w-full border rounded-md py-2 px-3 focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Sources</option>
                  {leadSources.map((source, index) => (
                    <option key={index} value={source}>{source}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  className="mr-2"
                  onClick={() => setFilters({
                    riskMin: 0,
                    riskMax: 100,
                    portfolioMin: 0,
                    portfolioMax: 10000000,
                    dateRange: 'all',
                    leadStatus: 'all',
                    leadSource: 'all'
                  })}
                >
                  Reset Filters
                </Button>
                <Button onClick={() => refetch()}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Prospects Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('last_name')}
                >
                  <div className="flex items-center">
                    <span>Name</span>
                    {getSortIcon('last_name')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('portfolio_value')}
                >
                  <div className="flex items-center">
                    <span>Portfolio Value</span>
                    {getSortIcon('portfolio_value')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('risk_score')}
                >
                  <div className="flex items-center">
                    <span>Risk Score</span>
                    {getSortIcon('risk_score')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Asset Allocation
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('submission_date')}
                >
                  <div className="flex items-center">
                    <span>Submission Date</span>
                    {getSortIcon('submission_date')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('lead_status')}
                >
                  <div className="flex items-center">
                    <span>Status</span>
                    {getSortIcon('lead_status')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('assigned_to')}
                >
                  <div className="flex items-center">
                    <span>Assigned To</span>
                    {getSortIcon('assigned_to')}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('follow_up_date')}
                >
                  <div className="flex items-center">
                    <span>Follow-up</span>
                    {getSortIcon('follow_up_date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <RefreshCw className="h-6 w-6 animate-spin text-primary" />
                    </div>
                    <p className="mt-2 text-gray-500">Loading prospects...</p>
                  </td>
                </tr>
              ) : filteredProspects.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center">
                    <p className="text-gray-500">No prospects found matching your criteria</p>
                  </td>
                </tr>
              ) : (
                filteredProspects.map((prospect) => (
                  <tr key={prospect.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {prospect.first_name} {prospect.last_name}
                          </div>
                          <div className="flex flex-col text-xs text-gray-500">
                            <div className="flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {prospect.email}
                            </div>
                            <div className="flex items-center">
                              <Phone className="h-3 w-3 mr-1" />
                              {prospect.phone}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(prospect.portfolio_value)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {prospect.lead_source}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskScoreColor(prospect.risk_score)}`}>
                          {prospect.risk_score}
                        </span>
                        <button 
                          className="ml-2 text-primary hover:text-primary-dark text-xs"
                          onClick={() => {
                            setSelectedProspect(prospect);
                            setShowRiskProfileModal(true);
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-32 h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div className="flex h-full">
                          <div 
                            className="bg-blue-500" 
                            style={{ width: `${prospect.asset_allocation.stocks}%` }}
                            title={`Stocks: ${prospect.asset_allocation.stocks}%`}
                          ></div>
                          <div 
                            className="bg-green-500" 
                            style={{ width: `${prospect.asset_allocation.bonds}%` }}
                            title={`Bonds: ${prospect.asset_allocation.bonds}%`}
                          ></div>
                          <div 
                            className="bg-yellow-500" 
                            style={{ width: `${prospect.asset_allocation.cash}%` }}
                            title={`Cash: ${prospect.asset_allocation.cash}%`}
                          ></div>
                          <div 
                            className="bg-purple-500" 
                            style={{ width: `${prospect.asset_allocation.other}%` }}
                            title={`Other: ${prospect.asset_allocation.other}%`}
                          ></div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>S: {prospect.asset_allocation.stocks}%</span>
                        <span>B: {prospect.asset_allocation.bonds}%</span>
                        <span>C: {prospect.asset_allocation.cash}%</span>
                        <span>O: {prospect.asset_allocation.other}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(prospect.submission_date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <select
                          value={prospect.lead_status}
                          onChange={(e) => updateLeadStatus.mutate({ 
                            prospectId: prospect.id, 
                            status: e.target.value as RiskProspect['lead_status'] 
                          })}
                          className="block w-full rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="qualified">Qualified</option>
                          <option value="unqualified">Unqualified</option>
                          <option value="converted">Converted</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prospect.assigned_to || (
                        <button 
                          className="text-primary hover:text-primary-dark text-xs"
                          onClick={() => {
                            setSelectedProspect(prospect);
                            setShowAssignModal(true);
                          }}
                        >
                          Assign
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {prospect.follow_up_date ? (
                        format(new Date(prospect.follow_up_date), 'MMM d, yyyy')
                      ) : (
                        <button 
                          className="text-primary hover:text-primary-dark text-xs"
                          onClick={() => {
                            setSelectedProspect(prospect);
                            setShowFollowUpModal(true);
                          }}
                        >
                          Schedule
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative">
                        <button
                          onClick={() => setOpenActionMenu(openActionMenu === prospect.id ? null : prospect.id)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                        
                        {openActionMenu === prospect.id && (
                          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1" role="menu" aria-orientation="vertical">
                              <button
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setSelectedProspect(prospect);
                                  setShowRiskProfileModal(true);
                                }}
                              >
                                View Risk Profile
                              </button>
                              <button
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setSelectedProspect(prospect);
                                  setShowAssignModal(true);
                                }}
                              >
                                Assign Lead
                              </button>
                              <button
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setSelectedProspect(prospect);
                                  setShowNoteModal(true);
                                }}
                              >
                                Add Note
                              </button>
                              <button
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setSelectedProspect(prospect);
                                  setShowFollowUpModal(true);
                                }}
                              >
                                Schedule Follow-up
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Risk Score Distribution</h3>
          <div className="h-64">
            <RiskScoreDistributionChart prospects={prospects || []} />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Lead Source Trends</h3>
          <div className="h-64">
            <LeadSourceChart prospects={prospects || []} />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Conversion Metrics</h3>
          <div className="h-64">
            <ConversionMetricsChart prospects={prospects || []} />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Portfolio Value Distribution</h3>
          <div className="h-64">
            <PortfolioValueChart prospects={prospects || []} />
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedProspect && (
        <>
          <AssignLeadModal
            isOpen={showAssignModal}
            onClose={() => setShowAssignModal(false)}
            prospect={selectedProspect}
            onAssign={(assignee) => assignLead.mutate({ 
              prospectId: selectedProspect.id, 
              assignee 
            })}
          />
          
          <AddNoteModal
            isOpen={showNoteModal}
            onClose={() => setShowNoteModal(false)}
            prospect={selectedProspect}
            onAddNote={(note) => addNote.mutate({ 
              prospectId: selectedProspect.id, 
              note 
            })}
          />
          
          <ScheduleFollowUpModal
            isOpen={showFollowUpModal}
            onClose={() => setShowFollowUpModal(false)}
            prospect={selectedProspect}
            onSchedule={(date) => scheduleFollowUp.mutate({ 
              prospectId: selectedProspect.id, 
              date 
            })}
          />
          
          <RiskProfileModal
            isOpen={showRiskProfileModal}
            onClose={() => setShowRiskProfileModal(false)}
            prospect={selectedProspect}
          />
        </>
      )}

      {/* Import Modal */}
      <ImportRiskProspectsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />
    </div>
  );
};

export default RiskNumberProspects;